import React, { useEffect, useState } from "react"
import { Button, Modal } from "react-bootstrap"

import { id_accessor } from "../../../../components/shared_components_registration"
import {
  adjustDateValue,
  getCurrentTime,
  getTimestampDifference,
  isNumber,
  stringifyDate,
} from "../../../utils"
import { getObjectsList } from "../../../../Loaders"
import { logToServer } from "../../../../ErrorBoundary"
import { Required } from "../../../widgets/utils"
import ReactSelectionElement from "../../../widgets/ReactSelectionElement"
import InfoPopover from "../../../widgets/InfoPopover"
import TagFormElement from "./TagFormElement"
import ReportGenerationTable from "./ReportGenerationTable"
import { borderStyle, registration } from "./utils"

export default function ReportGenerationModal({
  intl,
  availableRuns = [],
  plotList = [],
  siteMetrics = [],
  siteReactors = [],
  show,
  onHide,
}) {
  const maxMinutes = 5256000 // Ten years in minutes... An extreme upper limit but to prevent even more extreme inputs from users
  const [selectedRuns, setSelectedRuns] = useState([])
  const [averageMinutes, setAverageMinutes] = useState(1440)
  const [invalidForm, setInvalidForm] = useState(false)
  const [data, setData] = useState({})
  const [dataLoading, setDataLoading] = useState({})
  const [loadErr, setLoadErr] = useState({})
  const [selectedMetrics, setSelectedMetrics] = useState([])
  const [sortedRuns, setSortedRuns] = useState([])
  useEffect(() => {
    const runs = availableRuns
      .filter((r) => r.reactor)
      .sort((a, b) => a["reactor"].localeCompare(b["reactor"]))
    setSelectedMetrics(siteMetrics)
    setSelectedRuns(runs.map((r) => r[id_accessor]).slice(0, 4))
    setSortedRuns(runs)
  }, [])
  const [selectedTimes, setSelectedTimes] = useState([])
  const [useObsMaxTime, setUseObsMaxTime] = useState(false)
  useEffect(() => {
    // Initialize modal with some default timestamps that are pre-selected based on commonly used ts
    const previousTimes = []
    const previousDate = stringifyDate(adjustDateValue(getCurrentTime(), "day", 1, false), false)
    const currentTimes = ["06:00:00"]
    const currentDate = stringifyDate(getCurrentTime(), false)
    setSelectedTimes([
      ...previousTimes.map((time) => `${previousDate} ${time}`),
      ...currentTimes.map((time) => `${currentDate} ${time}`),
    ])
  }, [])

  useEffect(() => {
    setInvalidForm(
      selectedTimes.length !== selectedTimes.filter((t) => t).length ||
        selectedRuns.length === 0 ||
        (selectedTimes.length === 0 && !useObsMaxTime) ||
        !averageMinutes ||
        averageMinutes === 0 ||
        selectedMetrics.length === 0,
    )
  }, [selectedRuns, selectedTimes, useObsMaxTime, averageMinutes])

  const getRun = (run_id) => sortedRuns.find((r) => r[id_accessor] === parseInt(run_id)) || {}

  const getRunTimestamps = (run_id) => {
    /* Utility function to add observation_max timestamp to the list of timestamps to request or display if the user
     * chose the option to grab data based on observation_max as a reference ts (which is unique per run and thus
     * needs to be handled in a run specific manner */
    const obsMax = stringifyDate(getRun(run_id)?.observation_max || null)
    return [...selectedTimes, useObsMaxTime ? obsMax : null].filter((ts) => ts)
  }

  const submitForm = (e) => {
    // Query DB for recent_mean info for selected runs, reference timestamps, and metrics
    e.preventDefault()

    if (!invalidForm) {
      setData({})
      setLoadErr({})
      setDataLoading({})
      selectedRuns.forEach((run_id) => {
        setDataLoading((prevState) => {
          return {
            ...prevState,
            [run_id]: true,
          }
        })
        const encodedDate = encodeURIComponent(
          getRunTimestamps(run_id)
            .map((time) => stringifyDate(time, true, true))
            .join(","),
        )
        const tags = []
        const units = []
        const filters = []
        selectedMetrics.forEach((m) => {
          tags.push(m.tag)
          units.push(m.units)
          filters.push(`${isNumber(m.min) ? m.min : ""}__${isNumber(m.max) ? m.max : ""}`)
        })
        const url = `/warehouse/${registration.url_part}/${run_id}/recent_mean/?tag=${tags.join(
          ",",
        )}&units=${units.join(",")}&hours=${(averageMinutes / 60).toFixed(
          10,
        )}&maxtimes=${encodedDate}&filters=${filters.join(",")}`
        getObjectsList(url)
          .then((resp) => {
            const runData = {}
            resp.forEach((d) => {
              Object.entries(d["mean"]).forEach(([tag_name, tag_info]) => {
                if (!Object.keys(runData).includes(tag_name)) {
                  runData[tag_name] = {}
                }
                runData[tag_name][d["query_ts"]] = tag_info
              })
            })
            setData((prevState) => {
              return {
                ...prevState,
                [run_id]: runData,
              }
            })
            setDataLoading((prevState) => {
              return {
                ...prevState,
                [run_id]: false,
              }
            })
          })
          .catch((err) => {
            console.error(err)
            logToServer(err)
            setLoadErr((prevState) => {
              return {
                ...prevState,
                [run_id]: data,
              }
            })
            setDataLoading((prevState) => {
              return {
                ...prevState,
                [run_id]: false,
              }
            })
          })
      })
    }
  }

  const timestampCount = selectedTimes.length + (useObsMaxTime ? 1 : 0)

  const [selectionDisabled, setSelectionDisabled] = useState(false)
  useEffect(() => {
    /*
     * The UI starts to get squished if too many timestamps and runs are selected to display at once.
     *
     * Limiting it to <= 12 items since the current logic for the UI is based around using and equally
     * divvying up the 12 column system between the selected runs and timestamps
     */
    let count = timestampCount
    count *= selectedRuns.length
    setSelectionDisabled(count >= 12)
  }, [selectedRuns, selectedTimes, useObsMaxTime])

  const isLoading = Object.values(dataLoading).filter((load) => load).length > 0

  const runDetails = [
    [
      "observation_max_day",
      registration.run_length_label(intl),
      (runInfo) => (isNumber(runInfo.observation_max_day) ? runInfo.observation_max_day.toFixed(2) : "-"),
    ],
    [
      "observation_max",
      registration.latest_data_label(intl),
      (runInfo) => stringifyDate(runInfo.inoculated_on),
    ],
    [
      "inoculated_on",
      registration.inoculated_on_label(intl),
      (runInfo) => stringifyDate(runInfo.observation_max),
    ],
  ]
  const [runHeaderFields, setRunHeaderFields] = useState(["observation_max_day"])

  return (
    <Modal
      size={"xl"}
      dialogClassName={"bigger-modal"}
      show={show}
      onHide={onHide}
      style={{ overflowX: "scroll", overflowY: "scroll" }}
      backdrop={"static"}
      id={"shift-report-modal"}
      enforceFocus={false} // To allow ObsSelector Popover input element to have focus
    >
      <Modal.Header closeButton>
        <Modal.Title>{registration.widget_title(intl)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {Object.keys(loadErr).length > 0 && <div>{registration.err_label(intl)}</div>}
        <div id={"report-generation-menu"} style={{ padding: "15px", borderRadius: "5px", ...borderStyle }}>
          <div className={"row"}>
            <div className={"col-5"} style={{ borderRight: "solid var(--lanzalake-grey) 1px" }}>
              <div>
                <ReactSelectionElement
                  name={"runs"}
                  label={registration.choose_reactors(intl)}
                  list={
                    selectionDisabled
                      ? []
                      : sortedRuns.map((r) => ({
                          value: r[id_accessor],
                          label: `${r.reactor} (${r.name})`,
                        }))
                  }
                  value={sortedRuns
                    .filter((r) => selectedRuns.includes(r[id_accessor]))
                    .map((r) => ({
                      value: r[id_accessor],
                      label: `${r.reactor} (${r.name})`,
                    }))}
                  getOptionLabel={(obj) => obj.label}
                  onValueChange={(_, runs = []) => setSelectedRuns(runs.map((r) => r.value))}
                  required={true}
                />
              </div>
              <hr />
              <div>
                <div className={"row"} style={{ alignItems: "center" }}>
                  <div className={"col-8"}>
                    <label style={{ margin: "0" }}>{registration.choose_reference_times(intl)}:</label>
                    {selectedTimes.length === 0 && !useObsMaxTime && <Required />}
                  </div>
                  <div className={"col-4 text-end"}>
                    <Button
                      title={registration.add_new_time(intl)}
                      className={"btn btn-sm"}
                      variant={"outline-primary"}
                      disabled={selectionDisabled || selectedRuns.length === 0}
                      onClick={(_) => setSelectedTimes([...selectedTimes, stringifyDate(getCurrentTime())])}
                    >
                      <span className="fa fa-plus-circle" />
                    </Button>
                  </div>
                </div>
                <div className={"row"} style={{ marginBottom: "5px" }}>
                  <div className={"col-3"} style={{ padding: "0" }} />
                  <div className={"col-4"} style={{ display: "flex" }}>
                    <input
                      type={"number"}
                      onWheel={(e) => e.target.blur()}
                      step={"1"}
                      min={"0"}
                      style={{
                        width: "100%",
                        border: `${averageMinutes ? "" : "3px solid var(--lanzalake-red)"}`,
                      }}
                      value={isNumber(averageMinutes) ? averageMinutes : ""}
                      onChange={(e) => {
                        e.preventDefault()
                        let value = e.target.value
                        if (value && value < 0) {
                          value = 0
                        } else if (value && value > maxMinutes) {
                          value = maxMinutes
                        }
                        setAverageMinutes(value ? parseFloat(value) : "")
                      }}
                    />
                    {!averageMinutes && <Required />}
                    &nbsp;
                    <InfoPopover desc={registration.averaged_over_minutes(intl)} />
                  </div>
                  <div className={"col-5"} style={{ padding: "0" }} />
                </div>
                <div>
                  {selectedTimes.map((timestamp, idx) => (
                    <div key={`ts${idx}`} className={"row"} style={{ margin: "0" }}>
                      <div className={"col-5"} style={{ padding: "0" }}>
                        <input
                          type={"datetime-local"}
                          step={"1"}
                          style={{ width: "100%", fontSize: "85%" }}
                          className={"form-control form-control-sm"}
                          value={
                            isNumber(averageMinutes)
                              ? stringifyDate(adjustDateValue(timestamp, "minute", averageMinutes, false))
                              : ""
                          }
                          disabled={!timestamp}
                          onChange={(e) => {
                            e.preventDefault()
                            // Need to stringify here to do a proper gte/lte comparison to 'timestamp'
                            let value = stringifyDate(e.target.value || timestamp)
                            if (value > timestamp) {
                              value = timestamp
                            }
                            const diff = getTimestampDifference(value, timestamp, true) || {}
                            let minutes = diff["minutes"] || 0
                            if (minutes > maxMinutes) {
                              minutes = maxMinutes
                            }
                            setAverageMinutes(minutes)
                          }}
                        />
                      </div>
                      <div className={"col-1 text-center"}>-</div>
                      <div className={"col-5"} style={{ padding: "0" }}>
                        <input
                          type={"datetime-local"}
                          step={"1"}
                          style={{
                            width: "100%",
                            border: `${timestamp ? "" : "3px solid var(--lanzalake-red)"}`,
                            fontSize: "85%",
                          }}
                          className={"form-control form-control-sm"}
                          value={timestamp || ""}
                          onChange={(e) => {
                            e.preventDefault()
                            const editedTimes = [...selectedTimes]
                            editedTimes[idx] = stringifyDate(e.target.value)
                            setSelectedTimes(editedTimes)
                          }}
                        />
                      </div>
                      <div className={"col-1"} style={{ padding: "0" }}>
                        <Button
                          style={{ float: "right" }}
                          variant={"outline-primary"}
                          className={"btn btn-sm"}
                          onClick={(e) => {
                            e.preventDefault()
                            const modTimes = [...selectedTimes]
                            modTimes.splice(idx, 1)
                            setSelectedTimes(modTimes)
                          }}
                        >
                          <span className="fa fa-minus-circle" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: "85%", marginTop: "5px" }}>
                  <input
                    type={"checkbox"}
                    checked={useObsMaxTime}
                    disabled={!useObsMaxTime && selectionDisabled}
                    onChange={(_) => setUseObsMaxTime(!useObsMaxTime)}
                  />
                  &nbsp;
                  {registration.use_obs_max_label(intl)}
                  &nbsp;
                  <InfoPopover
                    desc={
                      <div>
                        {sortedRuns.map((r, r_idx) => (
                          <div key={`${r_idx}ObsMax`} style={{ fontSize: "90%" }}>{`${r.reactor} (${
                            r.name
                          }): ${stringifyDate(r.observation_max)}`}</div>
                        ))}
                      </div>
                    }
                  />
                </div>
              </div>
              <hr />
              <div>
                <ReactSelectionElement
                  name={"run-metadata"}
                  label={registration.choose_run_details_label(intl)}
                  list={runDetails.map((r) => ({
                    value: r[0],
                    label: r[1],
                  }))}
                  value={runDetails
                    .filter((d) => runHeaderFields.includes(d[0]))
                    .map((r) => ({
                      value: r[0],
                      label: r[1],
                    }))}
                  getOptionLabel={(obj) => obj.label}
                  onValueChange={(_, details = []) => setRunHeaderFields(details.map((d) => d.value))}
                  required={false}
                />
              </div>
            </div>
            <div className={"col-7"}>
              <TagFormElement
                intl={intl}
                selectedMetrics={selectedMetrics}
                setSelectedMetrics={setSelectedMetrics}
                plotList={plotList}
                siteReactors={siteReactors}
              />
            </div>
          </div>
          <hr />
          <div style={{ marginRight: "0px", marginTop: "15px", display: "flex", justifyContent: "flex-end" }}>
            <Button
              style={{ marginLeft: "auto" }}
              variant={"outline-primary"}
              className={"btn btn-sm"}
              disabled={invalidForm || isLoading}
              onClick={(e) => submitForm(e)}
            >
              {isLoading ? (
                <div
                  className="spinner-border text-primary text-center"
                  style={{ width: "15px", height: "15px" }}
                  role="status"
                />
              ) : (
                <span className="fa fa-table" />
              )}
              &nbsp;
              {registration.submit_label(intl)}
            </Button>
          </div>
        </div>
        <hr />
        <ReportGenerationTable
          intl={intl}
          isLoading={isLoading}
          data={data}
          selectedRuns={selectedRuns}
          timestampCount={timestampCount}
          selectedMetrics={selectedMetrics}
          runDetails={runDetails.filter((d) => runHeaderFields.includes(d[0]))}
          averageMinutes={averageMinutes}
          getRunTimestamps={getRunTimestamps}
          getRun={getRun}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant={"outline-primary"} onClick={onHide}>
          {registration.close_label(intl)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
